import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'

import { getErrorResponseData } from '@app/routing/Session/ErrorResponse'

import { ChangeTradingAccountPasswordForm } from '@pages/Traders-Room/Trading-Accounts/ChangeTradingAccountPassword/ChangeTradingAccountPasswordForm'
import { ChangeTradingAccountPasswordVerifyPage } from '@pages/Traders-Room/Trading-Accounts/ChangeTradingAccountPassword/ChangeTradingAccountPasswordVerify.page'

import { useModal } from '@context/ModalsContext'
import { usePathHistoryContext } from '@context/PathHistoryContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'
import { useSnackbar } from '@context/SnackbarContext'

import { ResponseError, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { isJsonString } from '@utils/isJsonString'

import { TwoFAProviderEnum } from '@model/TwoFactorAuthentication'

import { ActionModal } from '@shared/modals/Action.modal'
import { PasswordVerificationTemplate } from '@shared/templates/PasswordVerificationTemplate'

export interface ChangeTradingAccountPasswordValues {
  tradingAccountId: string
  password: string
  changeInvestorPassword: boolean
  languageId?: string
}

interface ChangePasswordDataVerifyData {
  password: string
  token: string
}

const passwordMatchingErrorKeys = {
  investor_password_matching: 'Trading Account.Password should not match investor password',
  master_password_matching: 'Trading Account.Password should not match master password',
} as const

type PasswordMatchingErrorCode = keyof typeof passwordMatchingErrorKeys

const isPasswordMatchingErrorCode = (code: unknown): code is PasswordMatchingErrorCode => {
  return typeof code === 'string' && code in passwordMatchingErrorKeys
}

const useSearchParamsData = () => {
  const [searchParams] = useSearchParams()

  const changeInvestorPassword =
    searchParams.get('changeInvestorPassword')?.toLowerCase() === 'true'
  const changeTickmillTraderPassword =
    searchParams.get('changeTickmillTraderPassword')?.toLowerCase() === 'true'

  return { changeInvestorPassword, changeTickmillTraderPassword }
}

interface ChangeTradingAccountPasswordProps {
  title?: string
  tradingAccountId?: string
  isButtonDisabled?: boolean
  onSubmitSuccess?(): void
}

export const ChangeTradingAccountPasswordPage: React.FC<ChangeTradingAccountPasswordProps> = (
  props
) => {
  const { title, isButtonDisabled, onSubmitSuccess } = props

  const { tradingAccountId } = useParams<{ tradingAccountId: string }>()
  const locale = useSessionLanguage()
  const { t } = useTranslation()
  const apiClient = useApiClient(ClientApiClient)
  const { addSnackbar } = useSnackbar()
  const { navigateToPreviousPath } = usePathHistoryContext()

  const { changeInvestorPassword, changeTickmillTraderPassword } = useSearchParamsData()

  const [newPasswordData, setNewPasswordData] = useState<Partial<ChangePasswordDataVerifyData>>()
  const [changePasswordData, setChangePasswordData] = useState<ChangePasswordDataVerifyData>()
  const [error, setError] = useState<string | undefined>()

  const cancelModal = useModal(ActionModal)

  const handleSubmit = async (values?: ChangeTradingAccountPasswordValues) => {
    const newPassword = values?.password || newPasswordData?.password
    if (!tradingAccountId || !newPassword) return

    try {
      await apiClient.verifyDuplicatePassword({
        tradingAccountId,
        password: newPassword,
        changeInvestorPassword,
      })

      const result = await apiClient.get2FAActionConfirmationOtp(
        {
          typeId: TwoFAProviderEnum.Email,
          twoFactor: { code: '', token: '', typeId: TwoFAProviderEnum.Email },
        },
        locale
      )

      const token = result?.data?.token
      const resetPassword = values?.password || changePasswordData?.password

      if (!token) {
        return
      }

      if (!resetPassword) {
        return
      }

      setNewPasswordData({ password: newPassword, token })
      setChangePasswordData({ password: resetPassword, token })
      return true
    } catch (error: unknown) {
      const { errorMessage, errorCode } = getErrorResponseData(error as ResponseError)

      const parsedErrorCode = isJsonString(errorCode) ? JSON.parse(errorCode) : errorCode
      const parsedErrorMessage = isJsonString(errorMessage)
        ? JSON.parse(errorMessage)
        : errorMessage

      const code = parsedErrorCode?.code || parsedErrorCode
      const errorText = isPasswordMatchingErrorCode(code)
        ? t(passwordMatchingErrorKeys[code])
        : parsedErrorMessage?.message || parsedErrorMessage

      setError(errorText)
      addSnackbar.error({ message: errorText })
      return false
    }
  }

  const handlePasswordRestSubmit = async (values?: ChangeTradingAccountPasswordValues) => {
    const isSuccess = await handleSubmit(values)
    if (isSuccess) {
      addSnackbar.success({ message: t('Sign up.Your new code has been sent') })
    }
  }

  const resetChangePasswordData = () => {
    setChangePasswordData(undefined)
  }

  if (!tradingAccountId && !props.tradingAccountId) {
    return null
  }

  if (changePasswordData?.token && changePasswordData?.password) {
    return (
      <PasswordVerificationTemplate
        onBack={() => cancelModal.open({ onConfirm: navigateToPreviousPath })}
      >
        <ChangeTradingAccountPasswordVerifyPage
          tradingAccountId={tradingAccountId! || props.tradingAccountId!}
          token={changePasswordData.token}
          password={changePasswordData.password}
          changeInvestorPassword={changeInvestorPassword}
          changeTickmillTraderPassword={changeTickmillTraderPassword}
          resetChangePasswordData={resetChangePasswordData}
          onPasswordRestSubmit={handlePasswordRestSubmit}
          onPasswordRestSubmitSuccess={onSubmitSuccess}
        />
      </PasswordVerificationTemplate>
    )
  }

  return (
    <ChangeTradingAccountPasswordForm
      title={title}
      tradingAccountId={tradingAccountId! || props.tradingAccountId!}
      changeInvestorPassword={changeInvestorPassword}
      changeTickmillTraderPassword={changeTickmillTraderPassword}
      validationError={error}
      onSubmit={handleSubmit}
      isButtonDisabled={isButtonDisabled}
    />
  )
}
